import React from 'react';
import { Helmet } from 'react-helmet-async';

const Citations: React.FC = () => {
  // Mock data for citations
  const citationsData = {
    totalCitations: 1247,
    newThisMonth: 89,
    topSources: [
      { source: 'Google My Business', count: 234, quality: 'high' },
      { source: 'Yelp', count: 189, quality: 'high' },
      { source: 'Facebook', count: 156, quality: 'medium' },
      { source: 'TripAdvisor', count: 134, quality: 'high' },
      { source: 'LinkedIn', count: 98, quality: 'medium' }
    ],
    recentCitations: [
      {
        id: 1,
        source: 'Google My Business',
        url: 'https://g.page/your-business',
        title: 'Your Business Name - Reviews',
        date: '2025-08-12',
        status: 'verified',
        quality: 'high'
      },
      {
        id: 2,
        source: 'Yelp',
        url: 'https://www.yelp.com/biz/your-business',
        title: 'Your Business Name | Yelp',
        date: '2025-08-11',
        status: 'pending',
        quality: 'high'
      },
      {
        id: 3,
        source: 'Facebook',
        url: 'https://facebook.com/your-business',
        title: 'Your Business Name - Home | Facebook',
        date: '2025-08-10',
        status: 'verified',
        quality: 'medium'
      },
      {
        id: 4,
        source: 'TripAdvisor',
        url: 'https://tripadvisor.com/your-business',
        title: 'Your Business Name - Reviews',
        date: '2025-08-09',
        status: 'verified',
        quality: 'high'
      },
      {
        id: 5,
        source: 'LinkedIn',
        url: 'https://linkedin.com/company/your-business',
        title: 'Your Business Name | LinkedIn',
        date: '2025-08-08',
        status: 'verified',
        quality: 'medium'
      }
    ],
    citationQuality: {
      high: 67,
      medium: 28,
      low: 5
    },
    monthlyGrowth: [
      { month: 'Jan', citations: 1150, growth: 0 },
      { month: 'Feb', citations: 1180, growth: 2.6 },
      { month: 'Mar', citations: 1210, growth: 2.5 },
      { month: 'Apr', citations: 1247, growth: 3.1 }
    ]
  };

  const getQualityColor = (quality: string) => {
    switch (quality) {
      case 'high': return 'text-green-600 bg-green-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'verified': return 'text-green-600 bg-green-100';
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'broken': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <>
      <Helmet>
        <title>Citations - Dream Digi Platform</title>
      </Helmet>
      
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Citations</h1>
          <p className="text-gray-600">Track and manage your business citations across the web</p>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Citations</p>
                <p className="text-2xl font-bold text-gray-900">{citationsData.totalCitations.toLocaleString()}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <i className="fas fa-link text-blue-600 text-xl"></i>
              </div>
            </div>
            <div className="mt-4">
              <span className="text-green-600 text-sm font-medium">+3.1%</span>
              <span className="text-gray-500 text-sm ml-1">from last month</span>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">New This Month</p>
                <p className="text-2xl font-bold text-gray-900">{citationsData.newThisMonth}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <i className="fas fa-plus text-green-600 text-xl"></i>
              </div>
            </div>
            <div className="mt-4">
              <span className="text-green-600 text-sm font-medium">+12.5%</span>
              <span className="text-gray-500 text-sm ml-1">from last month</span>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">High Quality</p>
                <p className="text-2xl font-bold text-gray-900">{citationsData.citationQuality.high}%</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <i className="fas fa-star text-purple-600 text-xl"></i>
              </div>
            </div>
            <div className="mt-4">
              <span className="text-green-600 text-sm font-medium">+2.3%</span>
              <span className="text-gray-500 text-sm ml-1">from last month</span>
            </div>
          </div>
        </div>

        {/* Monthly Growth Chart */}
        <div className="bg-white rounded-lg shadow p-6 border border-gray-200 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly Growth</h3>
          <div className="space-y-4">
            {citationsData.monthlyGrowth.map((month, index) => (
              <div key={index} className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600">{month.month}</span>
                <div className="flex items-center space-x-6">
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">{month.citations.toLocaleString()}</p>
                    <p className="text-xs text-gray-500">citations</p>
                  </div>
                  <div className="text-right">
                    <p className={`text-sm font-medium ${month.growth > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {month.growth > 0 ? '+' : ''}{month.growth}%
                    </p>
                    <p className="text-xs text-gray-500">growth</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top Sources and Recent Citations */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Top Sources */}
          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Citation Sources</h3>
            <div className="space-y-4">
              {citationsData.topSources.map((source, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getQualityColor(source.quality)}`}>
                      {source.quality.charAt(0).toUpperCase() + source.quality.slice(1)}
                    </span>
                    <span className="text-sm font-medium text-gray-900">{source.source}</span>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">{source.count}</p>
                    <p className="text-xs text-gray-500">citations</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Citations */}
          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Citations</h3>
            <div className="space-y-4">
              {citationsData.recentCitations.map((citation) => (
                <div key={citation.id} className="border-l-4 border-gray-200 pl-4">
                  <div className="flex items-start justify-between mb-2">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(citation.status)}`}>
                      {citation.status.charAt(0).toUpperCase() + citation.status.slice(1)}
                    </span>
                    <span className="text-xs text-gray-500">{citation.date}</span>
                  </div>
                  <h4 className="text-sm font-medium text-gray-900 mb-1">{citation.title}</h4>
                  <p className="text-xs text-blue-600 mb-2">{citation.source}</p>
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getQualityColor(citation.quality)}`}>
                      {citation.quality.charAt(0).toUpperCase() + citation.quality.slice(1)} Quality
                    </span>
                    <a href={citation.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      View Citation
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Citation Quality Distribution */}
        <div className="bg-white rounded-lg shadow p-6 border border-gray-200 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Citation Quality Distribution</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-3 bg-green-100 rounded-full flex items-center justify-center">
                <span className="text-2xl font-bold text-green-600">{citationsData.citationQuality.high}%</span>
              </div>
              <h4 className="font-medium text-gray-900 mb-1">High Quality</h4>
              <p className="text-sm text-gray-500">Verified and consistent</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-3 bg-yellow-100 rounded-full flex items-center justify-center">
                <span className="text-2xl font-bold text-yellow-600">{citationsData.citationQuality.medium}%</span>
              </div>
              <h4 className="font-medium text-gray-900 mb-1">Medium Quality</h4>
              <p className="text-sm text-gray-500">Some inconsistencies</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-3 bg-red-100 rounded-full flex items-center justify-center">
                <span className="text-2xl font-bold text-red-600">{citationsData.citationQuality.low}%</span>
              </div>
              <h4 className="font-medium text-gray-900 mb-1">Low Quality</h4>
              <p className="text-sm text-gray-500">Needs attention</p>
            </div>
          </div>
        </div>

        {/* AI Insights */}
        <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-6 border border-green-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <i className="fas fa-robot text-green-600 mr-2"></i>
            AI Citation Insights
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-lg p-4 border border-green-200">
              <h4 className="font-medium text-gray-900 mb-2">Citation Growth</h4>
              <p className="text-sm text-gray-600">Your citations are growing steadily at 3.1% monthly. Focus on high-quality directories for better SEO impact.</p>
            </div>
            <div className="bg-white rounded-lg p-4 border border-green-200">
              <h4 className="font-medium text-gray-900 mb-2">Quality Improvement</h4>
              <p className="text-sm text-gray-600">67% of citations are high quality. Consider fixing inconsistencies in business information across platforms.</p>
            </div>
            <div className="bg-white rounded-lg p-4 border border-green-200">
              <h4 className="font-medium text-gray-900 mb-2">Top Performers</h4>
              <p className="text-sm text-gray-600">Google My Business and Yelp are your top citation sources. Maintain consistency across these platforms.</p>
            </div>
            <div className="bg-white rounded-lg p-4 border border-green-200">
              <h4 className="font-medium text-gray-900 mb-2">Opportunities</h4>
              <p className="text-sm text-gray-600">Consider expanding to industry-specific directories and local business listings for broader reach.</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Citations;
